import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { useStoreSubscriptionsQuery } from "@/modules/stores/services/queries/useStoreSubscriptionsQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { StoreSubscriptionActionsMenu } from "@/modules/stores/components/subscriptions/StoreSubscriptionActionsMenu";
import {
  Button,
  Combobox,
  EmptyState,
  LoadingSkeleton,
  ErrorState,
} from "@a2z/ui";

type AdminStoreSubscriptionsPageClientProps = {
  locale: Locale;
  limit: number;
  offset: number;
  overdue: boolean;
  selectedPlanId: string;
};

function buildQuery(params: {
  limit: number;
  offset: number;
  overdue?: boolean;
  planId?: string;
}) {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.overdue) {
    query.set("overdue", "true");
  }
  if (params.planId) {
    query.set("planId", params.planId);
  }
  return query.toString();
}

function formatDate(value: string | null, locale: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

function formatAmount(
  value: number | null | undefined,
  locale: string,
  currency?: string,
) {
  if (value === null || value === undefined) {
    return "-";
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EGP",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} ${currency ?? ""}`.trim();
  }
}

export function StoreSubscriptionsPage({
  locale,
  limit,
  offset,
  overdue,
  selectedPlanId,
}: AdminStoreSubscriptionsPageClientProps) {
  const t = useTranslations();
  const [planIdValue, setPlanIdValue] = useState(selectedPlanId);
  const { data, isLoading, error, refetch } = useStoreSubscriptionsQuery({
    limit,
    offset,
    overdue,
    planId: selectedPlanId,
  });
  const { data: plansData } = useSubscriptionsQuery();

  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  const plans = plansData?.items ?? [];

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const adminKeys = keys.admin as typeof keys.admin & {
    storeSubscriptionsFilterPlan: string;
    storeSubscriptionsFilterPlanAll: string;
  };

  const hasPrev = offset > 0;
  const hasNext = offset + limit < data.total;
  const prevOffset = Math.max(offset - limit, 0);
  const nextOffset = offset + limit;

  return (
    <section className="flex w-full max-w-5xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.admin.storeSubscriptionsTitle)}
          </h1>
        </div>
      </div>

      <form
        action={`/${locale}/admin/stores/subscriptions`}
        method="get"
        className="flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm"
      >
        <input type="hidden" name="limit" value={limit} />
        <input type="hidden" name="offset" value={0} />
        <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
          <input
            type="checkbox"
            name="overdue"
            value="true"
            defaultChecked={overdue}
            className="h-4 w-4"
          />
          {t(keys.admin.storeSubscriptionsFilterOverdue)}
        </label>
        <div className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
          <span className="text-[color:var(--text-muted)]">
            {t(adminKeys.storeSubscriptionsFilterPlan)}
          </span>
          <input type="hidden" name="planId" value={planIdValue} />
          <Combobox
            options={[
              {
                value: "",
                label: t(adminKeys.storeSubscriptionsFilterPlanAll),
              },
              ...plans.map((plan) => ({
                value: plan.id,
                label: pickLocalized(locale, plan.nameAr, plan.nameEn),
              })),
            ]}
            value={planIdValue}
            onChange={(value) => setPlanIdValue(value ?? "")}
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          className="h-9 rounded-full px-3 text-xs font-semibold transition max-lg:h-10"
        >
          {t(keys.common.applyFilters)}
        </Button>
        {overdue || selectedPlanId ? (
          <Link
            to={`/${locale}/admin/stores/subscriptions?${buildQuery({
              limit,
              offset: 0,
            })}`}
            className="text-xs font-semibold text-[color:var(--text-muted)]"
          >
            {t(keys.common.clearFilters)}
          </Link>
        ) : null}
      </form>

      {data.items.length === 0 ? (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.admin.storeSubscriptionsEmptySubtitle)}
        />
      ) : (
        <div className="grid gap-3">
          {data.items.map((item) => (
            <div
              key={item.storeId}
              className="relative rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-4 pr-24 text-sm transition hover:bg-[color:var(--hover)]"
            >
              <div className="absolute right-4 top-4">
                <StoreSubscriptionActionsMenu
                  storeId={item.storeId}
                  locale={locale}
                  isFreeTrial={item.isFreeTrial}
                />
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/${locale}/admin/stores/${item.storeId}?tab=subscriptions`}
                      className="text-sm font-semibold text-[color:var(--text-primary)]"
                    >
                      {item.storeName}
                    </Link>
                    {item.isOverdue ? (
                      <span className="rounded-full bg-[color:var(--error-100)] px-2 py-0.5 text-[13px] font-semibold text-[color:var(--error-500)]">
                        {t(keys.admin.storeSubscriptionsOverdueLabel)}
                      </span>
                    ) : null}
                    {item.isPartiallyPaid ? (
                      <span className="rounded-full bg-[color:var(--warning-100)] px-2 py-0.5 text-[13px] font-semibold text-[color:var(--warning-700)]">
                        {t(keys.admin.storeSubscriptionsPartialPaidLabel)}
                      </span>
                    ) : null}
                    {item.isFreeTrial ? (
                      <span className="rounded-full bg-[color:var(--success-100)] px-2 py-0.5 text-[13px] font-semibold text-[color:var(--success-600)]">
                        {t(keys.admin.storeSubscriptionsFreeTrialLabel)}
                      </span>
                    ) : null}
                  </div>
                  <div className="grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.storeSubscriptionsPlan)}
                      </div>
                      <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {pickLocalized(
                          locale,
                          item.planNameAr,
                          item.planNameEn,
                        )}
                      </div>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.storeSubscriptionsAmount)}
                      </div>
                      <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {formatAmount(item.amount, locale, item.currency)}
                      </div>
                    </div>
                    {item.creditBalance > 0 ? (
                      <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                        <div className="text-[13px] text-[color:var(--text-muted)]">
                          {t(keys.admin.storeSubscriptionsCreditBalance)}
                        </div>
                        <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                          {formatAmount(
                            item.creditBalance,
                            locale,
                            item.currency,
                          )}
                        </div>
                      </div>
                    ) : null}
                    <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.storeSubscriptionsPeriod)}
                      </div>
                      <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {item.periodDays}
                      </div>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.storeSubscriptionsNextPayment)}
                      </div>
                      <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {formatDate(item.nextPaymentDate, locale)}
                      </div>
                    </div>
                    <div className="rounded-lg border border-[color:var(--border-light)] px-3 py-2">
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.storeSubscriptionsLastPayment)}
                      </div>
                      <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                        {formatDate(item.lastPaymentAt, locale)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        {hasPrev ? (
          <Link
            to={`/${locale}/admin/stores/subscriptions?${buildQuery({
              limit,
              offset: prevOffset,
              overdue,
              planId: selectedPlanId || undefined,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.previous)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.previous)}
          </span>
        )}
        {hasNext ? (
          <Link
            to={`/${locale}/admin/stores/subscriptions?${buildQuery({
              limit,
              offset: nextOffset,
              overdue,
              planId: selectedPlanId || undefined,
            })}`}
            className="text-[color:var(--text-secondary)]"
          >
            {t(keys.common.next)}
          </Link>
        ) : (
          <span className="text-[color:var(--text-disabled)]">
            {t(keys.common.next)}
          </span>
        )}
      </div>
    </section>
  );
}
